import { loadAtlasMeta, atlasBust, type AssetCategory, type AtlasMeta, type SpriteGroup } from './atlas.js';
import { cropIconSprite } from './iconAtlas.js';
import { tibiaColor } from './outfit.js';
import type { OutfitColors } from './outfitIcon.js';

/**
 * Animated portraits for the menus (reference: prey, hunts, store, outfit
 * window, cyclopedia — creatures and outfits walk, magic items shimmer).
 * Builds every phase of one pose — the walking cycle (moving group, phases
 * 1…n) or the idle animation (idle group) — recoloured and with addons, all
 * frames sharing one anchor so the figure does not jitter.
 */

export type LookKind = 'creatures' | 'outfits' | 'mounts' | 'items';

export interface LookFrames {
  frames: HTMLCanvasElement[];
  /** ms per frame */
  durations: number[];
  width: number;
  height: number;
  /** Union of the opaque pixels of every frame (for trimmed portraits). */
  bounds: { x: number; y: number; w: number; h: number } | null;
}

export interface LookOptions {
  /** Walking cycle (moving group); otherwise the idle animation. */
  walking?: boolean;
  direction?: number;
  colors?: OutfitColors | null;
  addon?: number;
  /** Phase length for walking cycles (the atlas timings are speed-driven in game). */
  walkPhaseMs?: number;
  /**
   * The first phase only, even for appearances that do not animate (a still
   * portrait drawn on a canvas — no PNG encoding, unlike the *IconUrl helpers).
   */
  still?: boolean;
  /** Outfits: the mounted pose (pattern depth 1), like the game world draws a rider. */
  mounted?: boolean;
  /** Keep the sprites' own anchor (bottom-right) instead of centring them: for stacking a rider on a mount. */
  raw?: boolean;
  /**
   * A small atlas (same metadata contract) read first, e.g. 'about-outfits' (tools/extractor/build_about_atlas.py):
   * the page then never waits for the multi-MB full outfit index. Falls back to the full atlas.
   */
  atlas?: string;
}

const GROUP_IDLE = '0';
const GROUP_MOVING = '1';
const cache = new Map<string, Promise<LookFrames | null>>();
const pageImages = new Map<string, Promise<HTMLImageElement>>();

function loadImage(url: string): Promise<HTMLImageElement> {
  const existing = pageImages.get(url);
  if (existing) return existing;
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`page ${url}`));
    image.src = url;
  });
  promise.catch(() => pageImages.delete(url));
  pageImages.set(url, promise);
  return promise;
}

/** Item atlas metadata lives at /assets/items.json (same shape as the others). */
function metaFor(kind: LookKind): Promise<AtlasMeta> {
  return loadAtlasMeta(kind as AssetCategory);
}

const smallMetas = new Map<string, Promise<AtlasMeta | null>>();
/** A small atlas's metadata (null when the file is missing: the full atlas is used). */
function smallMeta(name: string): Promise<AtlasMeta | null> {
  let promise = smallMetas.get(name);
  if (!promise) {
    promise = fetch(`/assets/${name}.json`, { cache: 'no-cache' })
      .then((r) => (r.ok ? (r.json() as Promise<AtlasMeta>) : null))
      .catch(() => null);
    smallMetas.set(name, promise);
  }
  return promise;
}

async function cropFrom(meta: AtlasMeta, spriteId: number): Promise<ImageData | null> {
  const frame = meta.atlas.frames[String(spriteId)];
  if (!frame) return null;
  const [page, x, y, w, h] = frame;
  const file = meta.atlas.pages[page];
  if (!file) return null;
  const image = await loadImage(`/assets/${file}?v=${atlasBust(meta)}`);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(image, x, y, w, h, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

async function cropFull(kind: LookKind, spriteId: number, small?: AtlasMeta | null): Promise<ImageData | null> {
  if (small) {
    const own = await cropFrom(small, spriteId);
    if (own) return own;
    // Blank in the small atlas (an empty mask row): nothing to fetch from the full one.
    if ((small.atlas as { absent?: number[] }).absent?.includes(spriteId)) return null;
  }
  if (kind === 'creatures' || kind === 'outfits') return cropIconSprite(kind, spriteId);
  const meta = await metaFor(kind).catch(() => null);
  const frame = meta?.atlas.frames[String(spriteId)];
  if (!meta || !frame) return null;
  const [page, x, y, w, h] = frame;
  const file = meta.atlas.pages[page];
  if (!file) return null;
  const image = await loadImage(`/assets/${file}?v=${atlasBust(meta)}`);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(image, x, y, w, h, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

function spriteIndex(group: SpriteGroup, phase: number, direction: number, addon: number, layer: number, depth = 0): number {
  const p = phase % Math.max(1, group.frames);
  const dir = direction % Math.max(1, group.patternWidth);
  const add = addon % Math.max(1, group.patternHeight);
  const lay = layer % Math.max(1, group.layers);
  const z = depth % Math.max(1, group.patternDepth);
  return ((((p * group.patternDepth + z) * group.patternHeight + add) * group.patternWidth + dir) * group.layers) + lay;
}

function recolor(base: ImageData, mask: ImageData | null, colors: OutfitColors): ImageData {
  const palette = [tibiaColor(colors.head), tibiaColor(colors.body), tibiaColor(colors.legs), tibiaColor(colors.feet)];
  const pixels = new Uint8ClampedArray(base.data);
  if (mask && mask.data.length === pixels.length) {
    const marks = mask.data;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3]! < 8) continue;
      const r = marks[i]!;
      const g = marks[i + 1]!;
      const b = marks[i + 2]!;
      const tint = r > 80 && g > 80 && b < 80 ? palette[0]
        : r > 80 && g < 80 && b < 80 ? palette[1]
          : g > 80 && r < 80 && b < 80 ? palette[2]
            : b > 80 && r < 80 && g < 80 ? palette[3]
              : null;
      if (!tint) continue;
      // Tibia/OTClient outfit colouring: the template pixel times the colour, per
      // channel (the reference shows exactly this — no extra brightening).
      pixels[i] = Math.round((pixels[i]! * tint[0]) / 255);
      pixels[i + 1] = Math.round((pixels[i + 1]! * tint[1]) / 255);
      pixels[i + 2] = Math.round((pixels[i + 2]! * tint[2]) / 255);
    }
  }
  return new ImageData(pixels, base.width, base.height);
}

function opaqueBounds(image: ImageData): { x: number; y: number; w: number; h: number } | null {
  let minX = image.width;
  let minY = image.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < image.height; y += 1) {
    for (let x = 0; x < image.width; x += 1) {
      if (image.data[(y * image.width + x) * 4 + 3]! < 8) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return maxX < 0 ? null : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

async function build(kind: LookKind, lookType: number, options: LookOptions): Promise<LookFrames | null> {
  const small = options.atlas ? await smallMeta(options.atlas) : null;
  const meta = small?.entries[String(lookType)] ? small : await metaFor(kind).catch(() => null);
  const entry = meta?.entries[String(lookType)];
  if (!entry) return null;
  const groups = entry.groups;
  const group = options.walking
    ? groups[GROUP_MOVING] ?? groups[GROUP_IDLE] ?? Object.values(groups)[0]
    : groups[GROUP_IDLE] ?? Object.values(groups)[0];
  if (!group || (group.frames <= 1 && !options.still)) return null;
  // Old 3-phase walk groups start with the standing pose (walk = 1…n); the
  // protobuf appearances' 8-phase moving group is all walking, like the
  // reference renderer's (every phase of fixedFrameGroup 1).
  const movingGroup = options.walking && group === groups[GROUP_MOVING];
  const phases: number[] = [];
  for (let phase = movingGroup && group.frames > 2 && group.frames <= 3 ? 1 : 0; phase < group.frames; phase += 1) {
    phases.push(phase);
    if (options.still) break;
  }
  const direction = options.direction ?? 2;
  const rows = [0];
  const addon = options.addon ?? 0;
  if (addon & 1) rows.push(1);
  if (addon & 2) rows.push(2);
  const usable = rows.filter((row) => row < Math.max(1, group.patternHeight));
  const images: ImageData[] = [];
  for (const phase of phases) {
    let composed: ImageData | null = null;
    for (const row of usable) {
      const depth = options.mounted ? 1 : 0;
      const baseId = group.sprites[spriteIndex(group, phase, direction, row, 0, depth)];
      if (baseId === undefined) continue;
      const base = await cropFull(kind, baseId, small);
      if (!base) continue;
      let layer = base;
      if (options.colors && group.layers > 1) {
        const maskId = group.sprites[spriteIndex(group, phase, direction, row, 1, depth)];
        const mask = maskId !== undefined ? await cropFull(kind, maskId, small) : null;
        layer = recolor(base, mask, options.colors);
      }
      if (!composed) composed = new ImageData(new Uint8ClampedArray(layer.data), layer.width, layer.height);
      else {
        const ox = Math.floor((composed.width - layer.width) / 2);
        const oy = Math.floor((composed.height - layer.height) / 2);
        for (let y = 0; y < layer.height; y += 1) {
          for (let x = 0; x < layer.width; x += 1) {
            const si = (y * layer.width + x) * 4;
            if (layer.data[si + 3]! < 8) continue;
            const dx = x + ox;
            const dy = y + oy;
            if (dx < 0 || dy < 0 || dx >= composed.width || dy >= composed.height) continue;
            const di = (dy * composed.width + dx) * 4;
            composed.data[di] = layer.data[si]!;
            composed.data[di + 1] = layer.data[si + 1]!;
            composed.data[di + 2] = layer.data[si + 2]!;
            composed.data[di + 3] = layer.data[si + 3]!;
          }
        }
      }
    }
    if (composed) images.push(composed);
  }
  if (images.length < (options.still ? 1 : 2)) return null;
  const width = Math.max(...images.map((image) => image.width));
  const height = Math.max(...images.map((image) => image.height));
  // One anchor for all frames: centre the union of their opaque pixels.
  let union: { x: number; y: number; w: number; h: number } | null = null;
  for (const image of images) {
    const box = opaqueBounds(image);
    if (!box) continue;
    if (!union) union = { ...box };
    else {
      const x2 = Math.max(union.x + union.w, box.x + box.w);
      const y2 = Math.max(union.y + union.h, box.y + box.h);
      union.x = Math.min(union.x, box.x);
      union.y = Math.min(union.y, box.y);
      union.w = x2 - union.x;
      union.h = y2 - union.y;
    }
  }
  const shiftX = union && kind !== 'items' && !options.raw ? Math.floor((width - union.w) / 2) - union.x : 0;
  const shiftY = union && kind !== 'items' && !options.raw ? Math.floor((height - union.h) / 2) - union.y : 0;
  const frames = images.map((image) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.putImageData(image, shiftX, shiftY);
    return canvas;
  });
  const walkMs = options.walkPhaseMs ?? 110;
  const durations = phases.map((phase) => {
    if (movingGroup) return walkMs;
    const timing = group.phases?.[phase];
    return timing ? Math.max(60, (timing[0] + timing[1]) / 2) : 200;
  });
  const bounds = union ? { x: union.x + shiftX, y: union.y + shiftY, w: union.w, h: union.h } : null;
  return { frames, durations, width, height, bounds };
}

/** Every phase of a pose, or null when the appearance does not animate. Cached. */
export function lookFrames(kind: LookKind, lookType: number, options: LookOptions = {}): Promise<LookFrames | null> {
  const colors = options.colors;
  const key = [
    kind, lookType, options.walking ? 'walk' : 'idle', options.direction ?? 2, options.addon ?? 0,
    colors ? `${colors.head}-${colors.body}-${colors.legs}-${colors.feet}` : 'plain', options.walkPhaseMs ?? 110,
    options.still ? 'still' : 'anim', options.mounted ? 'mounted' : '', options.raw ? 'raw' : '', options.atlas ?? '',
  ].join(':');
  let promise = cache.get(key);
  if (!promise) {
    promise = build(kind, lookType, options).catch(() => null);
    cache.set(key, promise);
    if (cache.size > 600) {
      const first = cache.keys().next().value;
      if (first !== undefined) cache.delete(first);
    }
  }
  return promise;
}
